import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Receipt as ReceiptIcon, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  paid: { label: "Bezahlt", cls: "bg-accent/15 text-accent" },
  open: { label: "Offen", cls: "bg-amber-500/15 text-amber-500" },
  overdue: { label: "Überfällig", cls: "bg-destructive/15 text-destructive" },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const newItem = () => ({ _key: Math.random().toString(36).slice(2), description: "", qty: 1, price: 0 });
  const emptyForm = { customer_id: "", tax_rate: 19, status: "open", due_date: "", items: [ newItem() ] };
  const [form, setForm] = useState(emptyForm);

  async function load() {
    const [i, u] = await Promise.all([api.get("/invoices"), api.get("/users").catch(()=>({data:[]}))]);
    setInvoices(i.data); setUsers(u.data);
  }
  useEffect(()=>{ load(); }, []);

  async function save() {
    try {
      const payload = { ...form,
        customer_id: form.customer_id || null,
        tax_rate: parseFloat(form.tax_rate),
        items: form.items.map(it => ({ description: it.description, qty: parseFloat(it.qty)||0, price: parseFloat(it.price)||0 })),
      };
      await api.post("/invoices", payload);
      toast.success("Rechnung erstellt"); setOpen(false); setForm(emptyForm); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  }

  async function markPaid(inv) { await api.patch(`/invoices/${inv.id}`, { status: "paid" }); load(); }

  function download(inv) {
    const cust = users.find(u=>u.id===inv.customer_id);
    const text = `RECHNUNG ${inv.number}\n\nKunde: ${cust?.name || "—"}\nDatum: ${new Date(inv.created_at).toLocaleDateString('de-DE')}\n${inv.due_date ? `Fällig: ${inv.due_date}\n` : ""}\n\nPOSITIONEN:\n${inv.items.map(it=>`- ${it.description}: ${it.qty} × € ${it.price.toFixed(2)} = € ${(it.qty*it.price).toFixed(2)}`).join("\n")}\n\nZwischensumme: € ${inv.subtotal.toFixed(2)}\nSteuer (${inv.tax_rate}%): € ${inv.tax.toFixed(2)}\nGESAMT: € ${inv.total.toFixed(2)}\n\nStatus: ${STATUS[inv.status]?.label}`;
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${inv.number}.txt`; a.click();
  }

  const subtotal = form.items.reduce((s,it)=>s + (parseFloat(it.qty)||0)*(parseFloat(it.price)||0), 0);
  const tax = subtotal * (parseFloat(form.tax_rate)||0) / 100;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Abrechnung</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Rechnungen</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-11 px-6 font-semibold" data-testid="btn-new-invoice"><Plus className="w-4 h-4 mr-1" /> Rechnung erstellen</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>Neue Rechnung</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kunde</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger className="rounded-full h-10 px-4" data-testid="inv-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                    <SelectContent>{users.filter(u=>u.role==="customer").map(u=><SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fälligkeit</Label><Input type="date" value={form.due_date} onChange={e=>setForm({...form, due_date:e.target.value})} className="rounded-full h-10 px-4" data-testid="inv-due" /></div>
              </div>
              <div>
                <Label>Positionen</Label>
                <div className="space-y-2 mt-2">
                  {form.items.map((it, idx) => (
                    <div key={it._key} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-6 rounded-full h-9" placeholder="Beschreibung" value={it.description} onChange={e=>{ const arr=[...form.items]; arr[idx].description=e.target.value; setForm({...form, items:arr}); }} data-testid={`inv-item-desc-${idx}`} />
                      <Input className="col-span-2 rounded-full h-9" type="number" placeholder="Menge" value={it.qty} onChange={e=>{ const arr=[...form.items]; arr[idx].qty=e.target.value; setForm({...form, items:arr}); }} />
                      <Input className="col-span-3 rounded-full h-9" type="number" placeholder="€" value={it.price} onChange={e=>{ const arr=[...form.items]; arr[idx].price=e.target.value; setForm({...form, items:arr}); }} />
                      <Button size="icon" variant="ghost" className="col-span-1 rounded-full" onClick={()=>setForm(f=>({...f, items: f.items.filter((_,j)=>j!==idx)}))}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-full" onClick={()=>setForm(f=>({...f, items:[...f.items, newItem()]}))} data-testid="inv-add-item"><Plus className="w-3 h-3 mr-1" /> Position</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Steuersatz (%)</Label><Input type="number" value={form.tax_rate} onChange={e=>setForm({...form, tax_rate:e.target.value})} className="rounded-full h-10 px-4" /></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                    <SelectTrigger className="rounded-full h-10 px-4"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-2xl bg-secondary/50 p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span>Zwischensumme</span><span>€ {subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Steuer</span><span>€ {tax.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-base"><span>Gesamt</span><span>€ {(subtotal+tax).toFixed(2)}</span></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full" data-testid="inv-save">Erstellen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {invoices.map(inv => {
          const cust = users.find(u=>u.id===inv.customer_id);
          return (
            <Card key={inv.id} className="rounded-3xl p-6 border-border/60 glass" data-testid={`invoice-${inv.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-11 h-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center"><ReceiptIcon className="w-5 h-5" /></div>
                <Badge className={`rounded-full ${STATUS[inv.status]?.cls}`}>{STATUS[inv.status]?.label}</Badge>
              </div>
              <div className="mt-3 font-heading font-semibold">{inv.number}</div>
              <div className="text-xs text-muted-foreground">{cust?.name || "—"}</div>
              <div className="text-2xl font-heading font-bold mt-2">€ {inv.total?.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">Inkl. € {inv.tax?.toFixed(2)} Steuer</div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={()=>download(inv)} data-testid={`inv-dl-${inv.id}`}><Download className="w-3 h-3 mr-1" /> Export</Button>
                {inv.status !== "paid" && <Button size="sm" className="rounded-full flex-1" onClick={()=>markPaid(inv)} data-testid={`inv-paid-${inv.id}`}>Bezahlt</Button>}
              </div>
            </Card>
          );
        })}
        {invoices.length === 0 && <div className="col-span-full text-center text-muted-foreground py-12">Noch keine Rechnungen.</div>}
      </div>
    </div>
  );
}
